// Settings Hook
// هوك الإعدادات

import { useState, useCallback } from 'react';
import settingsService from '../services/settingsService';

/**
 * Hook for managing application settings (now uses hardcoded values)
 * @returns {Object} Settings state and methods
 */
export const useSettings = () => {
  // Use hardcoded values immediately - no loading state needed
  const [settings] = useState(settingsService.getDefaultSettings());
  const [mapConfig] = useState(settingsService.getDefaultMapConfig());
  const [loading] = useState(false); // Always false since we use hardcoded values
  const [error] = useState(null); // Always null since no API calls

  /**
   * Load all settings (now returns hardcoded values immediately)
   */
  const loadSettings = useCallback(async () => {
    // No-op since we use hardcoded values
    return;
  }, []);

  /**
   * Load settings by group (now returns hardcoded values)
   */
  const loadSettingsByGroup = useCallback(async () => {
    // No-op since we use hardcoded values
    return;
  }, []);

  /**
   * Get a specific setting value (now returns hardcoded values)
   */
  const getSetting = useCallback(async (key, defaultValue = null) => {
    return await settingsService.getSetting(key) || defaultValue;
  }, []);

  /**
   * Refresh settings cache (no-op since no cache)
   */
  const refreshSettings = useCallback(() => {
    // No-op since we use hardcoded values
  }, []);

  /**
   * Force refresh settings (no-op since no API calls)
   */
  const forceRefreshSettings = useCallback(async () => {
    // No-op since we use hardcoded values
  }, []);

  // No need to load settings on mount since we use hardcoded values

  return {
    settings,
    mapConfig,
    loading,
    error,
    loadSettings,
    loadSettingsByGroup,
    getSetting,
    refreshSettings,
    forceRefreshSettings,
  };
};

/**
 * Hook specifically for map configuration (now uses hardcoded values)
 * @returns {Object} Map configuration state and methods
 */
export const useMapConfig = () => {
  // Use hardcoded values immediately - no loading state needed
  const [mapConfig] = useState(settingsService.getDefaultMapConfig());
  const [loading] = useState(false); // Always false since we use hardcoded values
  const [error] = useState(null); // Always null since no API calls

  /**
   * Load map configuration (now returns hardcoded values immediately)
   */
  const loadMapConfig = useCallback(async () => {
    // No-op since we use hardcoded values
    return;
  }, []);

  /**
   * Refresh map configuration (no-op since no cache)
   */
  const refreshMapConfig = useCallback(() => {
    // No-op since we use hardcoded values
  }, []);

  /**
   * Force refresh map configuration (no-op since no API calls)
   */
  const forceRefreshMapConfig = useCallback(async () => {
    // No-op since we use hardcoded values
  }, []);

  // No need to load map config on mount since we use hardcoded values

  return {
    mapConfig,
    loading,
    error,
    loadMapConfig,
    refreshMapConfig,
    forceRefreshMapConfig,
  };
};

/**
 * Hook for getting a single setting value (now uses hardcoded values)
 * @param {string} key - Setting key
 * @param {any} defaultValue - Default value if setting not found
 * @returns {Object} Setting value state
 */
export const useSetting = (key, defaultValue = null) => {
  // Get hardcoded value immediately
  const hardcodedValue = settingsService.getDefaultValue(key) || defaultValue;
  const [value] = useState(hardcodedValue);
  const [loading] = useState(false); // Always false since we use hardcoded values
  const [error] = useState(null); // Always null since no API calls

  const loadSetting = useCallback(async () => {
    // No-op since we use hardcoded values
    return;
  }, []);

  return {
    value,
    loading,
    error,
    refresh: loadSetting,
  };
};

export default useSettings;
